import { Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Link, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { externalFormStyling } from '../styles/sxStyling';
import _ from 'lodash';
import { EMAIL_REGEX } from '../utility/inputUtils';
import CustomAxios from '../utility/customAxios';
import { menuAppBarStyles } from '../components/layouts/menuAppBar/MenuAppBarStyles';

const GRECAPTCHA_CLIENT_ID = process.env.REACT_APP_GRECAPTCHA_CLIENT_ID;

interface CreateAccountState {
  org_name: string;
  org_email: string;
  org_phone: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

const BLANK_STATE = {
  org_name: '',
  org_email: '',
  org_phone: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
};

const REQUIRED_FIELDS: (keyof CreateAccountState)[] = ['org_name', 'org_email', 'first_name', 'last_name', 'email'];

function SignUpModal({ open, onClose }: { open: boolean, onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        Welcome
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography>Thank you for signing up for Schedulink.io! We&apos;re excited to have you on board.</Typography>
        <Typography>You should get an email from us in the next few minutes to verify your address. Just click the link in that email to finish setting up your account and get logged in.</Typography>
        <Typography>If you hit any snags or have questions, don&apos;t hesitate to check out our help center or contact our support team. We&apos;re here to help!</Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CreateAccount() {
  const [formState, setFormState] = useState<CreateAccountState>(BLANK_STATE);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [grecaptchaResponse, setGrecaptchaResponse] = useState<string | null>(null);

  const updateFormData = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!_.includes(_.keys(BLANK_STATE), e.target.id)) return;
    setFormState({ ...formState, [e.target.id]: e.target.value });
  };

  const isValid = useMemo(
    () => _.every(_.at(formState, REQUIRED_FIELDS), v => !!_.trim(v)) && EMAIL_REGEX.test(formState.org_email) && EMAIL_REGEX.test(formState.email) && grecaptchaResponse,
    [formState, grecaptchaResponse],
  );

  const onSubmit = () => {
    CustomAxios.post('organizations/create-account', { ...formState, captcha_id: grecaptchaResponse })
      .then(() => setModalOpen(true))
      .catch((_error) => alert("Unable to create the account. Please contact support."));
  };

  const onGrecaptchLoad = () => {
    // @ts-expect-error: Grecaptcha loaded at runtime - no typing
    const grecaptcha: any = window.grecaptcha;
    if (!grecaptcha) return;
    grecaptcha.render(
      "grecaptcha-container",
      {
        sitekey: GRECAPTCHA_CLIENT_ID,
        callback: setGrecaptchaResponse,
        'expired-callback': () => setGrecaptchaResponse(null),
      },
    );
  };

  useEffect(() => {
    // @ts-expect-error: Assigning component inner function to global scope
    window.onGrecaptchLoad = onGrecaptchLoad;
    const script = document.createElement('script');
    script.src = "https://www.google.com/recaptcha/api.js?onload=onGrecaptchLoad&render=explicit";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box component={Link} href="https://schedulink.io" sx={{ ...menuAppBarStyles.logoBox, mb: 4 }}>
          <Box component="img" src="/svg/schedulink_logo.svg" alt="logo" sx={menuAppBarStyles.logoImg} />
          <Typography sx={{ ...menuAppBarStyles.logoText, color: 'black' }}>ScheduLink</Typography>
        </Box>
        <Typography component="h1" variant="h5">
          Streamline Your Operations
        </Typography>
        <Divider />
        <Box sx={externalFormStyling}>
          <Typography>About your organization</Typography>
          <TextField
            autoFocus
            required
            label="Name"
            id="org_name"
            value={formState.org_name}
            onChange={updateFormData}
          />
          <TextField
            required
            label="Email"
            id="org_email"
            value={formState.org_email}
            onChange={updateFormData}
          />
          <TextField
            label="Phone (Optional)"
            id="org_phone"
            value={formState.org_phone}
            onChange={updateFormData}
          />
          <Divider />
          <Typography>About you</Typography>
          <TextField
            required
            label="First Name"
            id="first_name"
            value={formState.first_name}
            onChange={updateFormData}
          />
          <TextField
            required
            label="Last Name"
            id="last_name"
            value={formState.last_name}
            onChange={updateFormData}
          />
          <TextField
            required
            label="Email"
            id="email"
            value={formState.email}
            onChange={updateFormData}
          />
          <TextField
            label="Phone (Optional)"
            id="phone"
            value={formState.phone}
            onChange={updateFormData}
          />
          <Box id="grecaptcha-container" sx={{ display: 'flex', justifyContent: 'center' }} />
          <Typography variant='body2'>
            By signing up, you agree to Schedulink&apos;s <Link target="_blank" href="https://schedulink.io/terms-of-service">Terms of Service</Link> and acknowledge the Schedulink <Link target="_blank" href="https://schedulink.io/privacy-policy">Privacy Statement</Link>.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignContent: 'baseline', mb: 2 }}>
            <Button variant='outlined' component={Link} href="/login">I have an account</Button>
            <Button size='small' variant="contained" onClick={onSubmit} disabled={!isValid}>
              Sign Up
            </Button>
          </Box>

        </Box>
      </Box>
      <SignUpModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </Container >
  );
}